import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Applications_API, Category_API, Jobs_API, BASE_URL, ResumeDelete_API } from "./environment";
import { useAuth } from "../context/AuthContext";
import { FaTrash, FaFilter, FaTimes } from "react-icons/fa";
import { GrDocumentText } from "react-icons/gr";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";
import "./JobModal.css";

// Component for managing applications with delete, multiple delete, filter, and detail view functionality
export default function Applications() {
  const { userData } = useAuth();
  const [applications, setApplications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    id: [],
    name: [],
    email: [],
    mobile: [],
    date_applied: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterInputs, setFilterInputs] = useState({
    category: "",
    jobName: "",
    locations: "",
    req_id: "",
  });
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const columnDisplay = {
    id: "App ID",
    name: "Name",
    email: "Email",
    mobile: "Mobile",
    date_applied: "Applied Date",
  };

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${Category_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, [userData.access_token]);

  // Fetch jobs
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${Jobs_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch jobs");
        }
        const data = await response.json();
        setJobs(data);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      }
    };
    fetchJobs();
  }, [userData.access_token]);

  // Fetch applications based on filter state
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        let url = `${Applications_API}/getAll`;
        if (isFilterApplied) {
          const params = new URLSearchParams();
          if (filterInputs.jobName)
            params.append("jobName", filterInputs.jobName);
          if (filterInputs.category)
            params.append("category", filterInputs.category);
          if (filterInputs.locations)
            params.append("locations", filterInputs.locations);
          if (filterInputs.req_id) params.append("req_id", filterInputs.req_id);
          if (params.toString()) {
            url = `${Applications_API}/filter?${params.toString()}`;
          }
        }
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch applications");
        }
        const data = await response.json();
        setApplications(data);
      } catch (error) {
        console.error("Error fetching applications:", error);
        Swal.fire(
          "Error",
          "Failed to fetch applications. Please try again.",
          "error"
        );
      }
    };
    fetchApplications();
  }, [userData.access_token, isFilterApplied, filterInputs]);

  // Filtered jobs based on selected category
  const filteredJobs = filterInputs.category
    ? jobs.filter((job) => job.category === filterInputs.category)
    : jobs;

  // Get distinct values for a column
  const getDistinctValues = (column) => {
    const values = applications.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort((a, b) =>
      column === "id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply column filters to an item
  const applyFilters = (item) => {
    return (
      (filters.id.length === 0 || filters.id.includes(item.id)) &&
      (filters.name.length === 0 || filters.name.includes(item.name)) &&
      (filters.email.length === 0 || filters.email.includes(item.email)) &&
      (filters.mobile.length === 0 || filters.mobile.includes(item.mobile)) &&
      (filters.date_applied.length === 0 ||
        filters.date_applied.includes(item.date_applied))
    );
  };

  // Filter applications
  const filteredApplications = applications.filter(applyFilters);

  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const paginatedApplications = filteredApplications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Handle filter input change
  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilterInputs((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category" ? { jobName: "" } : {}),
    }));
  };

  // Handle apply filter button
  const handleApplyFilter = () => {
    setIsFilterApplied(true);
    setCurrentPage(1);
  };

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  // Select all filtered applications
  const handleSelectAll = () => {
    if (selectedRows.length === filteredApplications.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredApplications.map((item) => item.id));
    }
  };

  // Handle filter click for a column
  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  // Apply column filter selections
  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [showFilterPopup]: newSelected,
    }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete resume
  const deleteResume = async (path) => {
    if (!path) return;
    try {
      const response = await fetch(`${ResumeDelete_API}?path=${encodeURIComponent(path)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${userData.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete resume");
      }
    } catch (error) {
      console.error("Error deleting resume:", error);
      Swal.fire("Error", "Failed to delete resume. Please try again.", "error");
    }
  };

  // Delete multiple selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    Swal.fire({
      text: `Do you want to delete ${selectedRows.length} selected applications?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await Promise.all(
            selectedRows.map(async (id) => {
              // Fetch application details to get resume path
              const response = await fetch(`${Applications_API}/getById/${id}`, {
                headers: {
                  Authorization: `Bearer ${userData.access_token}`,
                  Accept: "application/json",
                },
              });
              if (!response.ok) {
                throw new Error(`Failed to fetch application ${id} details`);
              }
              const application = await response.json();
              const resumePath = application.resume;

              // Delete resume
              if (resumePath) {
                await deleteResume(resumePath);
              }

              // Delete application
              const deleteResponse = await fetch(`${Applications_API}/delete/${id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${userData.access_token}`,
                  Accept: "application/json",
                },
              });
              if (!deleteResponse.ok) {
                throw new Error(`Failed to delete application ${id}`);
              }
            })
          );
          const updatedApplications = applications.filter(
            (app) => !selectedRows.includes(app.id)
          );
          setApplications(updatedApplications);
          setSelectedRows([]);
          Swal.fire(
            "Success!",
            "Selected applications and their resumes deleted successfully",
            "success"
          );
        } catch (error) {
          console.error("Error deleting applications:", error);
          Swal.fire(
            "Error",
            "Failed to delete some applications or resumes. Please try again.",
            "error"
          );
        }
      }
    });
  };

  // Function to delete a single application with confirmation
  const deleteApplication = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        // Fetch application details to get resume path
        const response = await fetch(`${Applications_API}/getById/${id}`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch application ${id} details`);
        }
        const application = await response.json();
        const resumePath = application.resume;

        // Delete resume
        if (resumePath) {
          await deleteResume(resumePath);
        }

        // Delete application
        const deleteResponse = await fetch(`${Applications_API}/delete/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!deleteResponse.ok) {
          throw new Error("Failed to delete application");
        }
        setApplications(applications.filter((app) => app.id !== id));
        Swal.fire("Deleted!", "Application and its resume have been deleted.", "success");
      } catch (error) {
        console.error("Error deleting application:", error);
        Swal.fire(
          "Error",
          "Failed to delete application or resume. Please try again.",
          "error"
        );
      }
    }
  };

  // Open modal for view
  const viewApplication = (app) => {
    setSelectedApplication(app);
    setShowModal(true);
  };

  // Render filter popup for a column
  const renderFilterPopup = (column) => {
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter(
      (val) =>
        val != null &&
        val.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) =>
      tempSelected.includes(val)
    );

    const handleToggleVisible = (e) => {
      if (e.target.checked) {
        setTempSelected([...new Set([...tempSelected, ...filteredValues])]);
      } else {
        setTempSelected(
          tempSelected.filter((val) => !filteredValues.includes(val))
        );
      }
    };

    const clearSelections = () => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [column]: [],
      }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    return (
      <div
        className="filter-popup"
        style={{
          position: "absolute",
          top: "100%",
          left: "0",
          zIndex: 100,
        }}
      >
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={handleToggleVisible}
          />
          <input
            className="filter-search"
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <span className="clear-search" onClick={() => setSearchInput("")}>
              ×
            </span>
          )}
        </div>
        <div className="suggestions-list">
          {filteredValues.length === 0 ? (
            <div>No results found</div>
          ) : (
            filteredValues.map((val) => (
              <label key={val?.toString() || "null"}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(val)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setTempSelected([...tempSelected, val]);
                    } else {
                      setTempSelected(tempSelected.filter((v) => v !== val));
                    }
                  }}
                />
                {val != null ? val.toString() : "N/A"}
              </label>
            ))
          )}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Applications - SMTS Admin Panel</title>
      </Helmet>
      <div className="page-title">
        <h2>Applications</h2>
      </div>

      <div
        className="filter-section"
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "20px",
          alignItems: "center",
        }}
      >
        <select
          name="category"
          value={filterInputs.category}
          onChange={handleFilterInputChange}
          style={{ padding: "8px", width: "150px" }}
        >
          <option value="">Select Category</option>
          {categories.map((cat) => (
            <option key={cat.cat_id} value={cat.Name}>
              {cat.Name}
            </option>
          ))}
        </select>
        <select
          name="jobName"
          value={filterInputs.jobName}
          onChange={handleFilterInputChange}
          style={{ padding: "8px", width: "150px" }}
        >
          <option value="">Select Job Name</option>
          {filteredJobs.map((job) => (
            <option key={job.job_id} value={job.jobName}>
              {job.jobName}
            </option>
          ))}
        </select>
        <select
          name="locations"
          value={filterInputs.locations}
          onChange={handleFilterInputChange}
          style={{ padding: "8px", width: "150px" }}
        >
          <option value="">Select Location</option>
          <option value="India">India</option>
          <option value="USA">USA</option>
        </select>
        <input
          type="text"
          name="req_id"
          placeholder="Req ID"
          value={filterInputs.req_id}
          onChange={handleFilterInputChange}
          style={{ padding: "8px", width: "150px" }}
        />
        <button
          onClick={handleApplyFilter}
          style={{
            padding: "8px 16px",
            background: "#3085d6",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Apply
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredApplications.length &&
                    filteredApplications.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                {hasActiveFilters && (
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === filteredApplications.length &&
                      filteredApplications.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                )}
                App ID
                <FaFilter
                  className={`filter-icon ${
                    filters.id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "id" && renderFilterPopup("id")}
              </th>
              <th style={{ position: "relative" }}>
                Name
                <FaFilter
                  className={`filter-icon ${
                    filters.name.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("name")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "name" && renderFilterPopup("name")}
              </th>
              <th style={{ position: "relative" }}>
                Email
                <FaFilter
                  className={`filter-icon ${
                    filters.email.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("email")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "email" && renderFilterPopup("email")}
              </th>
              <th style={{ position: "relative" }}>
                Mobile
                <FaFilter
                  className={`filter-icon ${
                    filters.mobile.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("mobile")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "mobile" && renderFilterPopup("mobile")}
              </th>
              <th style={{ position: "relative" }}>
                Applied Date
                <FaFilter
                  className={`filter-icon ${
                    filters.date_applied.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("date_applied")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "date_applied" &&
                  renderFilterPopup("date_applied")}
              </th>
              <th>View</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedApplications.map((app) => (
              <tr
                key={app.id}
                className={selectedRows.includes(app.id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(app.id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(app.id)}
                    onChange={() => toggleRowSelection(app.id)}
                  />
                </td>
                <td>{app.id}</td>
                <td>{app.name}</td>
                <td>{app.email}</td>
                <td>{app.mobile}</td>
                <td>{app.date_applied}</td>
                <td>
                  <button
                    className="action-button"
                    onClick={() => viewApplication(app)}
                  >
                    <GrDocumentText />
                  </button>
                </td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteApplication(app.id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" title="Delete Selected" /> (
          {selectedRows.length})
        </button>
      )}

      {showModal && (
        <ApplicationModal
          application={selectedApplication}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

// ApplicationModal component for viewing applications
function ApplicationModal({ application, onClose }) {
  // Render simple field
  const renderField = (field, label) => {
    if (field === "resume" && application[field]) {
      const resumeUrl = `${BASE_URL}${application[field]}`;
      return (
        <tr>
          <td>{label}</td>
          <td>
            <a href={resumeUrl} target="_blank" rel="noopener noreferrer">
              {application[field]}
            </a>
          </td>
        </tr>
      );
    }

    return (
      <tr>
        <td>{label}</td>
        <td>{application[field] || "N/A"}</td>
      </tr>
    );
  };

  return (
    <div className="job-modal-overlay">
      <div className="job-modal">
        <div className="job-modal-header">
          <h2>Application Details ({application.id})</h2>
          <div className="job-modal-actions">
            <button className="job-modal-close" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>
        <table className="job-details-table">
          <tbody>
            {renderField("id", "ID")}
            {renderField("name", "Name")}
            {renderField("email", "Email")}
            {renderField("mobile", "Mobile")}
            {renderField("linkedin", "LinkedIn")}
            {renderField("coverletter", "Cover Letter")}
            {renderField("resume", "Resume")}
            {renderField("job_id", "Job ID")}
            {renderField("date_applied", "Date Applied")}
          </tbody>
        </table>
      </div>
    </div>
  );
}